import { GenerateSignaturePacketPageAllocationQuery, TransactionDetailsPageAllocationQuery } from '@/__generated__/graphql';
import { SortIcon } from '@/assets/icons/sortIcon';
import { BurgerMenuIcon } from '@/assets/static/icons/burger-menu';
import { CloseMenuIcon } from '@/assets/static/icons/close-menu';
import { InfoCircleIcon } from '@/assets/static/icons/info-circle';
import Tabs, { ITab, TabMobileWrap, TabsControl, TabsLine, TabTitle } from '@/components/basicComponents/tabs';
import { Tooltip } from '@/components/basicComponents/tooltip';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import { TableBodyWrapper, TableRowCell, TextCell } from '@/components/table/styles';
import { useResponsive } from '@/hooks/use-responsive';
import { getUTCDate } from '@/shared/utils/date';
import { themeVar } from '@/styles/theme';
import { EntityType } from '@trace-8/trace8-schema';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

export const getInvestorEntityType = (entityType: keyof typeof EntityType, otherType: string) => {
  if (!entityType) return '--';
  if (EntityType[entityType] === EntityType.Other && otherType) return otherType;

  return EntityType[entityType];
};

const statusTagColors = {
  'Internal Review Rejected': {
    color: '#991D29',
    bgColor: '#FFEAEC'
  },
  Outstanding: {
    color: '#CE7D03',
    bgColor: '#FFEACA'
  },
  Committed: {
    color: '#277326',
    bgColor: '#EFFFEA'
  },
  Terminated: {
    color: '#991D29',
    bgColor: '#FFEAEC'
  }
};

const getStatusTagContent = (detailsData: TransactionDetailsPageAllocationQuery['Allocation']) => {
  const statusTagContent = {
    'Internal Review Rejected': (
      <Tooltip
        title={
          <TableBodyWrapper>
            {detailsData?.complianceBy && (
              <TableRowCell width={100}>
                <TextCell>Internal Review Rejected by {`${detailsData?.complianceBy.firstName} ${detailsData?.complianceBy.lastName}`}</TextCell>
              </TableRowCell>
            )}
            {detailsData?.terminatedAt && (
              <TableRowCell width={100}>
                <TextCell>On {getUTCDate(new Date(detailsData.terminatedAt))}</TextCell>
              </TableRowCell>
            )}
            {detailsData?.suitability && (
              <TableRowCell width={100}>
                <TextCell>Suitability: {detailsData.suitability}</TextCell>
              </TableRowCell>
            )}
            {detailsData?.reason && (
              <TableRowCell width={100}>
                <TextCell>Reason: {detailsData.reason}</TextCell>
              </TableRowCell>
            )}
          </TableBodyWrapper>
        }
      >
        <StatusTagWithTooltip>
          Internal Review Rejected
          <InfoCircleIcon fill="#991D29" width={16} height={16} style={{ position: 'relative', top: '2px' }} />
        </StatusTagWithTooltip>
      </Tooltip>
    ),
    Outstanding: <>Pending - {detailsData?.operationsWorkflowTransitionTask?.name}</>,
    Committed: 'Committed',
    Terminated: (
      <Tooltip
        title={
          <TableBodyWrapper>
            {detailsData?.terminateBy && (
              <TableRowCell width={100}>
                <TextCell>Terminated by {`${detailsData?.terminateBy.firstName} ${detailsData?.terminateBy.lastName}`}</TextCell>
              </TableRowCell>
            )}
            {detailsData?.terminatedAt && (
              <TableRowCell width={100}>
                <TextCell>On {getUTCDate(new Date(detailsData.terminatedAt))}</TextCell>
              </TableRowCell>
            )}
            {detailsData?.terminationReason && (
              <TableRowCell width={100}>
                <TextCell>Reason: {detailsData.terminationReason}</TextCell>
              </TableRowCell>
            )}
            {detailsData?.terminationNote && (
              <TableRowCell width={100}>
                <TextCell>Comments: {detailsData.terminationNote}</TextCell>
              </TableRowCell>
            )}
          </TableBodyWrapper>
        }
      >
        <StatusTagWithTooltip>
          Terminated
          <InfoCircleIcon fill="#991D29" width={16} height={16} style={{ position: 'relative', top: '2px' }} />
        </StatusTagWithTooltip>
      </Tooltip>
    )
  };

  return statusTagContent[detailsData.allocationStatus as keyof typeof statusTagContent] || '';
};

interface DetailsProps {
  detailsData: GenerateSignaturePacketPageAllocationQuery['Allocation'] | TransactionDetailsPageAllocationQuery['Allocation'];
  withoutTabs?: boolean;
  withStatusTag?: boolean;
  tabs?: ITab[];
  activeTab?: string;
  isTabMenuOpen?: boolean;
  setIsTabMenuOpen?: Dispatch<SetStateAction<boolean>>;
  setActiveTab?: (value: string) => void;
}

export const Details = ({
  detailsData,
  withStatusTag = false,
  withoutTabs,
  tabs,
  activeTab,
  isTabMenuOpen,
  setIsTabMenuOpen,
  setActiveTab
}: DetailsProps) => {
  const { isMobile, isTablet, isDesktop, isLargeDesktop } = useResponsive();
  const [isDetailsOpen, setIsDetailsOpen] = useState(true);
  const { t } = useTranslation(['generateSignaturePacketPage', 'common']);

  return (
    <DetailsWrapper isOpen={isDetailsOpen} isMobile={isMobile}>
      <DetailsBoby isMobile={isMobile} isOpen={isDetailsOpen}>
        <ExpandableWrapper isMobile={isMobile} isOpen={isDetailsOpen}>
          <TopDetailsWrapper>
            {detailsData?.legalEntity?.entityName && <SubTitle>Subscription for {detailsData?.legalEntity?.entityName}</SubTitle>}
            {withStatusTag && 'allocationStatus' in detailsData && detailsData?.allocationStatus && (
              <StatusTag
                color={statusTagColors[detailsData?.allocationStatus as keyof typeof statusTagColors].color}
                bgColor={statusTagColors[detailsData?.allocationStatus as keyof typeof statusTagColors].bgColor}
              >
                {getStatusTagContent(detailsData as TransactionDetailsPageAllocationQuery['Allocation'])}
              </StatusTag>
            )}
          </TopDetailsWrapper>
          <TwoColumnWrapper isMobile={isMobile}>
            <DetailsItemWrapper>
              {t('generateSignaturePacketPage:details.amount')}
              <DetailsItemValue>
                {detailsData?.committedCapital ? <FormattingTooltip zIndex={1000}>{detailsData.committedCapital}</FormattingTooltip> : '--'}
              </DetailsItemValue>
            </DetailsItemWrapper>
            <DetailsItemWrapper>
              {t('generateSignaturePacketPage:details.vehicle')}
              <DetailsItemValue>
                {detailsData?.investmentVehicle && detailsData?.investmentVehicle?.name ? detailsData?.investmentVehicle?.name : '--'}
              </DetailsItemValue>
            </DetailsItemWrapper>
          </TwoColumnWrapper>
          <TwoColumnWrapper isMobile={isMobile}>
            <DetailsItemWrapper>
              {t('generateSignaturePacketPage:details.investorDocsDue')}
              <DetailsItemValue>
                {detailsData?.investment?.investorDocsDue ? getUTCDate(new Date(detailsData.investment.investorDocsDue)) : '--'}
              </DetailsItemValue>
            </DetailsItemWrapper>
            <DetailsItemWrapper>
              {t('generateSignaturePacketPage:details.vehicleTaxStatus')}
              <DetailsItemValue>
                {detailsData?.investmentVehicle && typeof detailsData?.investmentVehicle?.taxExempt === 'boolean'
                  ? detailsData?.investmentVehicle?.taxExempt
                    ? 'Exempt'
                    : 'Taxable'
                  : '--'}
              </DetailsItemValue>
            </DetailsItemWrapper>
          </TwoColumnWrapper>
          <TwoColumnWrapper isMobile={isMobile}>
            <DetailsItemWrapper>
              {t('generateSignaturePacketPage:details.investorEntityTaxStatus')}
              <DetailsItemValue>
                {typeof detailsData?.legalEntity?.taxExempt !== 'boolean' ? '--' : detailsData.legalEntity.taxExempt ? 'Exempt' : 'Taxable'}
              </DetailsItemValue>
            </DetailsItemWrapper>
            <DetailsItemWrapper>
              {t('generateSignaturePacketPage:details.investorEntityType')}
              <DetailsItemValue>{getInvestorEntityType(detailsData?.legalEntity?.entityType, detailsData?.legalEntity?.otherType)}</DetailsItemValue>
            </DetailsItemWrapper>
          </TwoColumnWrapper>
        </ExpandableWrapper>
      </DetailsBoby>

      <ControlDetails isMobile={isMobile} isOpen={isDetailsOpen}>
        {tabs && activeTab && setActiveTab && setIsTabMenuOpen && (
          <>
            {isDesktop || isTablet || isLargeDesktop ? (
              <>
                <Tabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />
                <TabsLine />
              </>
            ) : (
              <TabsControl isMobile={isMobile}>
                {tabs.length !== 1 && (
                  <>
                    {isTabMenuOpen ? (
                      <CloseMenuIcon
                        fill={themeVar('action.primary')}
                        onClick={() => setIsTabMenuOpen(!isTabMenuOpen)}
                        style={{ cursor: 'pointer' }}
                      />
                    ) : (
                      <BurgerMenuIcon
                        fill={themeVar('action.primary')}
                        onClick={() => setIsTabMenuOpen(!isTabMenuOpen)}
                        style={{ cursor: 'pointer' }}
                      />
                    )}
                  </>
                )}
                <TabMobileWrap onClick={() => setActiveTab(activeTab)} isActive>
                  <TabTitle isActive isMobile={isMobile}>
                    {activeTab}
                  </TabTitle>
                </TabMobileWrap>
              </TabsControl>
            )}
          </>
        )}

        <HideDetailsWrap isMobile={isMobile} withoutTabs={withoutTabs} onClick={() => setIsDetailsOpen(!isDetailsOpen)}>
          <ArrowBtn isRotate={isDetailsOpen}>
            <SortIcon fill={themeVar('action.primary')} />
          </ArrowBtn>
          <ControlDetailsTitle isMobile={isMobile}>{isDetailsOpen ? t('common:hideDetails') : t('common:showDetails')}</ControlDetailsTitle>
        </HideDetailsWrap>
      </ControlDetails>
    </DetailsWrapper>
  );
};

const DetailsWrapper = styled.div<{ isOpen: boolean; isMobile: boolean }>`
  display: flex;
  width: 100%;
  flex-direction: column;
  background-color: ${themeVar('layer.1')};
  border-bottom: 1px solid ${themeVar('border.base')};
  transition: 1s;
`;

const DetailsBoby = styled.div<{ isOpen: boolean; isMobile: boolean }>`
  opacity: ${({ isOpen }) => (isOpen ? '1' : '0')};
  display: grid;
  grid-template-rows: ${({ isOpen }) => (isOpen ? '1fr' : '0fr')};
  overflow: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  transition: grid-template-rows 300ms;
  padding: ${({ isMobile }) => (isMobile ? '10px 40px' : '10px  60px')};
  color: ${themeVar('font.base')};
`;

const ExpandableWrapper = styled.div<{ isMobile: boolean; isOpen: boolean }>`
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const TopDetailsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  padding: 5px 0;
`;

const SubTitle = styled.h3`
  font-size: 19px;
  font-weight: 600;
  line-height: 46.2px;
  color: ${themeVar('font.strong')};
`;

const StatusTag = styled.div<{ color: string; bgColor: string }>`
  font-size: 19px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  padding: 8px 20px;
  border-radius: 6px;
  color: ${({ color }) => color};
  background-color: ${({ bgColor }) => bgColor};
`;

const StatusTagWithTooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const TwoColumnWrapper = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-template-columns: ${({ isMobile }) => (isMobile ? '1fr' : '1fr 1fr')};
  gap: ${({ isMobile }) => (isMobile ? '3px' : '30px')};
`;

const DetailsItemWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  color: ${themeVar('font.base')};
  padding: 5px 20px;
  border-radius: 4px;
  background: ${themeVar('layer.base')};
`;

const DetailsItemValue = styled.div`
  font-weight: 600;
`;

const ControlDetails = styled.div<{ isMobile: boolean; isOpen: boolean }>`
  display: flex;
  justify-content: space-between;
  padding: ${({ isMobile }) => (isMobile ? '10px 40px' : '0 60px')};
`;

const HideDetailsWrap = styled.div<{ isMobile: boolean; withoutTabs: boolean }>`
  position: relative;
  top: ${({ isMobile, withoutTabs }) => (isMobile || withoutTabs ? '0' : '15px')};
  margin-left: auto;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 10px 30px;
  gap: 12.5px;
`;

const ArrowBtn = styled.div<{ isRotate: boolean }>`
  transform: ${({ isRotate }) => (isRotate ? 'rotate(180deg)' : 'rotate(0deg)')};
  display: flex;
  justify-content: center;
  align-items: center;
  transition: 0.3s;
`;

const ControlDetailsTitle = styled.div<{ isMobile: boolean }>`
  font-family: Blinker, serif;
  font-size: ${({ isMobile }) => (isMobile ? '13px' : '16px')};
  font-weight: 600;
  line-height: 22px;
  color: ${themeVar('action.primary')};
`;
